import { useState } from "react";
import { Link } from "react-router-dom";
import { Ellipsis, MapPin, Star, StarOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { DeleteConfirmDialog } from "@/components/shared/DeleteConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BusinessListItem,
  BusinessStatus,
  useDeleteBusiness,
  useToggleBusiness,
  useToggleBusinessFeatured,
  useVerifyBusiness,
} from "@/hooks/useBusinesses";
import { formatDate } from "@/utils/format";
import { LogoAvatar } from "@/components/shared/LogoAvatar";

import { PermissionGate } from "@/components/rbac/PermissionGate";

interface Props {
  row: BusinessListItem;
  onReject: (row: BusinessListItem) => void;
  showPhone?: boolean;
  showSubscription?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDeleteSuccess?: (id: string) => void;
}

export const BusinessRow = ({
  row,
  onReject,
  showPhone = true,
  showSubscription = true,
  isSelected = false,
  onToggleSelect,
  onDeleteSuccess,
}: Props) => {
  const toggle = useToggleBusiness();
  const toggleFeatured = useToggleBusinessFeatured();
  const verify = useVerifyBusiness();
  const del = useDeleteBusiness();
  const [delOpen, setDelOpen] = useState(false);

  const onToggle = (checked: boolean) => {
    toggle.mutate(
      { id: row.id, isVisible: checked },
      {
        onSuccess: () =>
          toast.success(
            `${row.businessName} is now ${checked ? "visible" : "hidden"}`,
          ),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const onToggleFeatured = () => {
    toggleFeatured.mutate(
      { id: row.id, isFeatured: !row.isFeatured },
      {
        onSuccess: () =>
          toast.success(
            `${row.businessName} is now ${!row.isFeatured ? "featured" : "unfeatured"}`,
          ),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const onVerify = (status: BusinessStatus) => {
    verify.mutate(
      { id: row.id, status },
      {
        onSuccess: () => toast.success(`Status updated to ${status}`),
        onError: (e: any) => {
          const code = e?.data?.code;
          if (code === "DOCUMENTS_NOT_REVIEWED") {
            toast.error("Mark all documents as viewed before approving.");
          } else {
            toast.error(e.message);
          }
        },
      },
    );
  };

  const onDelete = () => {
    del.mutate(row.id, {
      onSuccess: () => {
        setDelOpen(false);
        onDeleteSuccess?.(row.id);
        toast.success(`${row.businessName} deleted`);
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const ownerBusinessCount = row.owner._count?.businesses ?? 1;
  const rating = Number(row.avgRating ?? 0);
  const reviewCount = row.reviewCount ?? 0;

  return (
    <tr
      className={`border-t border-border hover:bg-muted/30 text-xs ${
        isSelected ? "bg-primary/5" : ""
      }`}
    >
      <td className="px-3 py-1 w-10">
        <input
          type="checkbox"
          aria-label={`Select ${row.businessName}`}
          checked={isSelected}
          onChange={() => onToggleSelect?.(row.id)}
          className="h-4 w-4 rounded border-border cursor-pointer"
        />
      </td>
      <td className="px-3 py-1 min-w-0">
        <div className="flex items-center gap-2">
          <LogoAvatar src={row.logoUrl} name={row.businessName} size="sm" />
          <div className="min-w-0">
            <Link
              to={`/businesses/${row.id}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.businessName}
            </Link>
            <div className="text-[11px] text-muted-foreground">
              {row.owner.name}
              {ownerBusinessCount === 1 ? (
                <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 text-primary px-1.5 py-0 text-[10px] font-medium">
                  Only business
                </span>
              ) : (
                <span className="ml-1.5 text-[10px] text-muted-foreground">
                  ({ownerBusinessCount} businesses)
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-1">
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-muted-foreground" />
          {row.city}
        </div>
      </td>
      {showPhone && (
        <td className="px-3 py-1 text-muted-foreground">{row.phone}</td>
      )}
      <td className="px-3 py-1">
        {reviewCount > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            {rating.toFixed(1)}{" "}
            <span className="text-muted-foreground">({reviewCount})</span>
          </span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </td>
      <td className="px-3 py-1">
        <StatusBadge status={row.status} />
      </td>
      {showSubscription && (
        <td className="px-3 py-1">
          {row.owner.subscriptions?.[0] ? (
            <StatusBadge status={row.owner.subscriptions[0].status} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      )}
      <td className="px-3 py-1">
        <Switch
          checked={row.isVisible}
          onCheckedChange={onToggle}
          disabled={toggle.isPending || row.status !== "APPROVED"}
          aria-label={`Toggle visibility for ${row.businessName}`}
        />
      </td>
      <td className="px-3 py-1">
        <button
          onClick={onToggleFeatured}
          disabled={toggleFeatured.isPending || row.status !== "APPROVED"}
          aria-label={`Toggle featured for ${row.businessName}`}
          className="disabled:opacity-40"
        >
          {row.isFeatured ? (
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
          ) : (
            <StarOff className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </td>
      <td className="px-3 py-1 text-xs text-muted-foreground">
        {formatDate(row.createdAt)}
      </td>
      <td className="px-3 py-1 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger className="p-1 rounded hover:bg-muted">
            <Ellipsis className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to={`/businesses/${row.id}`}>View profile</Link>
            </DropdownMenuItem>
            {row.status === "PENDING" ? (
              <>
                <DropdownMenuItem onClick={() => onVerify("APPROVED")}>
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onReject(row)}>
                  Reject
                </DropdownMenuItem>
              </>
            ) : null}
            {row.status === "APPROVED" ? (
              <DropdownMenuItem onClick={() => onVerify("SUSPENDED")}>
                Suspend
              </DropdownMenuItem>
            ) : null}
            {row.status === "SUSPENDED" || row.status === "REJECTED" ? (
              <DropdownMenuItem onClick={() => onVerify("APPROVED")}>
                Re-activate
              </DropdownMenuItem>
            ) : null}
            <PermissionGate module="businesses" action="delete">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDelOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Delete
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
      <DeleteConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title={`Delete "${row.businessName}"?`}
        description="This will permanently delete the business and all associated data. This action cannot be undone."
        onConfirm={onDelete}
        loading={del.isPending}
      />
    </tr>
  );
};
